<template>
  <div class="med-expiry" :class="{ 'med-expiry--full': isFull }">
    <div v-if="isFull" class="med-expiry__seg">
      <label :for="ids.day" class="med-expiry__seg-label">Day</label>
      <select :id="ids.day" v-model="selectedDay" class="med-expiry__select">
        <option :value="null" disabled>—</option>
        <option v-for="day in filteredDays" :key="day" :value="day" v-text="day"/>
      </select>
    </div>
    <div class="med-expiry__seg">
      <label :for="ids.month" class="med-expiry__seg-label">Month</label>
      <select :id="ids.month" v-model.number="selectedMonth" class="med-expiry__select" @change="onMonthChange">
        <option :value="null" disabled>—</option>
        <option v-for="(monthName, monthVal) in filteredMonths" :key="monthName" :value="monthVal" v-text="monthName"/>
      </select>
    </div>
    <div class="med-expiry__seg">
      <label :for="ids.year" class="med-expiry__seg-label">Year</label>
      <select :id="ids.year" v-model="selectedYear" class="med-expiry__select" @change="onYearChange">
        <option :value="null" disabled>—</option>
        <option v-for="year in YEARS" :key="year" :value="year" v-text="year"/>
      </select>
    </div>
  </div>
</template>

<script setup>
import {computed, nextTick, ref, toRefs, watch} from 'vue';
import {Str} from '@/utilities/random.js';
import dayjs from 'dayjs';

const emit = defineEmits(['input']);

const props = defineProps({
  color: {
    type: String,
    validator(value) {
      return !value || ['G', 'B', 'Y'].includes(value);
    },
  },
  value: {
    type: String,
    validator(value) {
      if (!value) {
        return true;
      }
      return dayjs(value, 'YYYY-MM-DD', true).isValid();
    },
  },
});

const today = dayjs();
const currentYear = today.year();
const currentMonth = today.month();
const currentDay = today.date();

const YEARS = Array.from({length: 20}, (_, i) => currentYear + i);

const MONTHS = {
  0: 'January', 1: 'February', 2: 'March', 3: 'April',
  4: 'May', 5: 'June', 6: 'July', 7: 'August',
  8: 'September', 9: 'October', 10: 'November', 11: 'December',
};

const getDaysInMonth = (year, month) => {
  if (!month) return 31;
  return dayjs().set('date', 1).set('year', year ?? currentYear).set('month', month).endOf('month').date();
};

const {color, value: modelValue} = toRefs(props);
const isFull = computed(() => color.value !== 'G');

const ids = computed(() => ({
  day: Str.random({length: 8, unique: true}),
  month: Str.random({length: 8, unique: true}),
  year: Str.random({length: 8, unique: true}),
}));

const selectedYear = ref(null);
const selectedMonth = ref(null);
const selectedDay = ref(null);

const filteredMonths = computed(() => {
  if (!selectedYear.value || selectedYear.value > currentYear) return MONTHS;
  const _months = {};
  for (const monthsKey in MONTHS) {
    if (Number(monthsKey) >= currentMonth) _months[monthsKey] = MONTHS[monthsKey];
  }
  return _months;
});

const filteredDays = computed(() => {
  const startDay = selectedYear.value === currentYear && selectedMonth.value === currentMonth ? currentDay : 1;
  const totalDays = getDaysInMonth(selectedYear.value, selectedMonth.value) + 1;
  return Array.from({length: totalDays - startDay}, (_, i) => startDay + i);
});

const onYearChange = () => {
  nextTick(() => {
    if (filteredMonths.value[selectedMonth.value]) return;
    selectedMonth.value = null;
    onMonthChange();
  });
};

const onMonthChange = () => {
  nextTick(() => {
    if (!filteredMonths.value[selectedMonth.value]) return;
    if (!isFull.value) {
      selectedDay.value = filteredDays.value[filteredDays.value.length - 1];
      return;
    }
    if (!filteredDays.value.includes(selectedDay.value)) selectedDay.value = null;
  });
};

watch([selectedDay, selectedMonth, selectedYear], ([day, month, year]) => {
  if (
    !Number.isInteger(year) || year < currentYear
    || !Number.isInteger(month) || !filteredMonths.value[month]
    || !Number.isInteger(day) || !filteredDays.value.includes(day)
  ) {
    emit('input', undefined);
    return;
  }
  const date = dayjs().set('year', year).set('month', month).set('date', day);
  if (!date.isValid()) { emit('input', undefined); return; }
  emit('input', date.format('YYYY-MM-DD'));
}, {immediate: true});

watch([isFull, selectedMonth], ([isFull, month]) => {
  if (!isFull && filteredMonths.value[month]) {
    const lastDay = dayjs().set('month', month).endOf('month').date();
    if (selectedDay.value !== lastDay) selectedDay.value = lastDay;
  }
}, {immediate: true});

watch(modelValue, (value) => {
  if (!value) return;
  const d = dayjs(value, 'YYYY-MM-DD', true);
  const todayFormatted = today.format('YYYY-MM-DD');
  if (!d.isValid() || d.isBefore(todayFormatted)) { emit('input', undefined); return; }
  if (selectedYear.value !== d.year()) selectedYear.value = d.year();
  if (selectedMonth.value !== d.month()) selectedMonth.value = d.month();
  if (selectedDay.value !== d.date()) selectedDay.value = d.date();
}, {immediate: true});
</script>

<style scoped>
.med-expiry {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  border: 1.5px solid #E7E4DA;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.med-expiry--full {
  grid-template-columns: 1fr 1fr 1fr;
}

.med-expiry:focus-within {
  border-color: #FFB000;
  box-shadow: 0 0 0 3px rgba(255, 176, 0, 0.12);
}

.med-expiry__seg {
  display: flex;
  flex-direction: column;
  position: relative;
  border-right: 1px solid #E7E4DA;
}

.med-expiry__seg:last-child {
  border-right: none;
}

.med-expiry__seg-label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  /* WCAG 1.4.3: was #9197A1 (~2.94:1 on white). #6B7280 clears AA. */
  color: #6B7280;
  padding: 8px 12px 0;
  pointer-events: none;
  line-height: 1;
}

.med-expiry__select {
  -webkit-appearance: none;
  appearance: none;
  border: none;
  outline: none;
  background: transparent;
  padding: 2px 28px 10px 12px;
  font-size: 14px;
  color: #15171C;
  width: 100%;
  cursor: pointer;
  /* custom chevron */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='none' stroke='%239197A1' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' d='M1 1l4 4 4-4'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 10px;
}

.med-expiry__select:focus {
  background-color: rgba(255, 176, 0, 0.04);
}
</style>
